/**
 * 🎯 Global Hotkeys System - Mews-level keyboard shortcuts
 * 
 * Inspired by:
 * - Mews: Full keyboard control (Cmd+B = new booking)
 * - Figma: Cmd+K command palette
 * - Notion: Slash commands
 */

import { useEffect, useCallback } from 'react';

interface UseGlobalHotkeysProps {
  // Navigation
  onCommandK?: () => void;       // Command palette
  onEscape?: () => void;         // Close modal/cancel
  
  // Booking actions (Mews-inspired)
  onNewBooking?: () => void;     // Cmd+B = new booking
  onSave?: () => void;           // Cmd+S = save
  onSearch?: () => void;         // Cmd+F = focus search
  onRefresh?: () => void;        // Cmd+R = refresh data
  
  // Navigation shortcuts
  onGoToDashboard?: () => void;  // Cmd+1 = dashboard
  onGoToGrid?: () => void;       // Cmd+2 = booking grid
  onGoToBookings?: () => void;   // Cmd+3 = bookings list
  onGoToGuests?: () => void;     // Cmd+4 = guests
  onGoToRates?: () => void;      // Cmd+5 = rates & pricing
  
  // Modal controls
  onNextTab?: () => void;        // Cmd+] = next tab in modal
  onPrevTab?: () => void;        // Cmd+[ = prev tab in modal
  
  // Undo/Redo (Opera-inspired)
  onUndo?: () => void;           // Cmd+Z = undo
  onRedo?: () => void;           // Cmd+Shift+Z = redo
  
  // Options
  enabled?: boolean;
  scope?: 'global' | 'modal';    // Scope для предотвращения конфликтов
}

export const useGlobalHotkeys = (props: UseGlobalHotkeysProps) => {
  const {
    onCommandK,
    onEscape,
    onNewBooking,
    onSave,
    onSearch,
    onRefresh,
    onGoToDashboard,
    onGoToGrid,
    onGoToBookings,
    onGoToGuests,
    onGoToRates,
    onNextTab,
    onPrevTab,
    onUndo,
    onRedo,
    enabled = true,
    scope = 'global'
  } = props;

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;
    
    // Игнорируем если фокус в input/textarea
    const target = event.target as HTMLElement;
    const isInputFocused = target.tagName === 'INPUT' || 
                           target.tagName === 'TEXTAREA' || 
                           target.isContentEditable;
    
    const cmd = event.metaKey || event.ctrlKey;
    const shift = event.shiftKey;
    
    // Escape - always works
    if (event.key === 'Escape') {
      onEscape?.();
      return;
    }
    
    // Cmd+K - Command palette (always works)
    if (cmd && event.key === 'k') {
      event.preventDefault();
      onCommandK?.();
      return;
    }
    
    // Skip other shortcuts if in input
    if (isInputFocused && event.key !== 'Escape') return;
    
    // ============================
    // BOOKING ACTIONS (Mews-inspired)
    // ============================
    
    // Cmd+B = New Booking
    if (cmd && event.key === 'b') {
      event.preventDefault();
      onNewBooking?.();
      return;
    }
    
    // Cmd+S = Save
    if (cmd && event.key === 's') {
      event.preventDefault();
      onSave?.();
      return;
    }
    
    // Cmd+F = Focus Search
    if (cmd && event.key === 'f') {
      event.preventDefault();
      onSearch?.();
      return;
    }
    
    // Cmd+R = Refresh (prevent browser refresh)
    if (cmd && event.key === 'r') {
      event.preventDefault();
      onRefresh?.();
      return;
    }
    
    // ============================
    // NAVIGATION (Number keys)
    // ============================
    
    // Cmd+1 = Dashboard
    if (cmd && event.key === '1') {
      event.preventDefault();
      onGoToDashboard?.();
      return;
    }
    
    // Cmd+2 = Booking Grid
    if (cmd && event.key === '2') {
      event.preventDefault();
      onGoToGrid?.();
      return;
    }
    
    // Cmd+3 = Bookings List
    if (cmd && event.key === '3') {
      event.preventDefault();
      onGoToBookings?.();
      return;
    }
    
    // Cmd+4 = Guests
    if (cmd && event.key === '4') {
      event.preventDefault();
      onGoToGuests?.();
      return;
    }
    
    // Cmd+5 = Rates & Pricing
    if (cmd && event.key === '5') {
      event.preventDefault();
      onGoToRates?.();
      return;
    }
    
    // ============================
    // MODAL TAB NAVIGATION
    // ============================
    
    // Cmd+] = Next Tab
    if (cmd && event.key === ']') {
      event.preventDefault();
      onNextTab?.();
      return;
    }
    
    // Cmd+[ = Previous Tab
    if (cmd && event.key === '[') {
      event.preventDefault();
      onPrevTab?.();
      return;
    }
    
    // ============================
    // UNDO/REDO (Opera-inspired)
    // ============================
    
    // Cmd+Z = Undo
    if (cmd && !shift && event.key === 'z') {
      event.preventDefault();
      onUndo?.();
      return;
    }
    
    // Cmd+Shift+Z = Redo
    if (cmd && shift && event.key === 'z') {
      event.preventDefault();
      onRedo?.();
      return;
    }
    
  }, [
    enabled, 
    onCommandK, onEscape, onNewBooking, onSave, onSearch, onRefresh,
    onGoToDashboard, onGoToGrid, onGoToBookings, onGoToGuests, onGoToRates,
    onNextTab, onPrevTab, onUndo, onRedo
  ]);

  useEffect(() => {
    if (!enabled) return;
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [enabled, handleKeyDown]);
};

/**
 * 🎯 Хук для отображения подсказок по горячим клавишам
 */
export const KEYBOARD_SHORTCUTS = {
  newBooking: { key: 'B', mod: 'Cmd', description: 'Новая бронь' },
  save: { key: 'S', mod: 'Cmd', description: 'Сохранить' },
  search: { key: 'F', mod: 'Cmd', description: 'Поиск' },
  refresh: { key: 'R', mod: 'Cmd', description: 'Обновить' },
  commandPalette: { key: 'K', mod: 'Cmd', description: 'Командная палитра' },
  escape: { key: 'Esc', mod: '', description: 'Закрыть/Отмена' },
  dashboard: { key: '1', mod: 'Cmd', description: 'Дашборд' },
  grid: { key: '2', mod: 'Cmd', description: 'Шахматка' },
  bookings: { key: '3', mod: 'Cmd', description: 'Бронирования' },
  guests: { key: '4', mod: 'Cmd', description: 'Гости' },
  rates: { key: '5', mod: 'Cmd', description: 'Тарифы' },
  nextTab: { key: ']', mod: 'Cmd', description: 'След. вкладка' },
  prevTab: { key: '[', mod: 'Cmd', description: 'Пред. вкладка' },
  undo: { key: 'Z', mod: 'Cmd', description: 'Отменить' },
  redo: { key: 'Z', mod: 'Cmd+Shift', description: 'Повторить' },
};

export type ShortcutKey = keyof typeof KEYBOARD_SHORTCUTS;
